import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { compose } from "msw";
const generateAvatarurl = process.env.REACT_APP_GENERATE_AVATAR_URL;
const domainpath = process.env.REACT_APP_API_DOMAIN_ENDPOINT;

function CollectionPage() {
  const { collectionId } = useParams();
  const navigate = useNavigate();
  const [mainCollectionNFT, setMainCollectionNFT] = useState(null);
  const [nfts, setNfts] = useState([]);
  const [Collection, setCollection] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchCollection() {
      try {
        setLoading(true);
        const res = await axios.get(
          `${domainpath}/api/art/collections/${collectionId}`
        );

        if (res.data.success && res.data.collections.length > 0) {
          const collection = res.data.collections[0];

          // ✅ Correct way to get valid nftData array
          const filteredNfts = collection.nfts
            .map((nft) => {
              if (nft.nftData) {
                return {
                  ...nft.nftData,
                  art_id: nft.art._id, // push original MongoDB _id as `art_id`
                };
              }
              return null;
            })
            .filter(Boolean);

          // ✅ Calculate total volume from prices
          const totalVolumeCalculated = filteredNfts.reduce((acc, nft) => {
            if (nft?.price) return acc + nft.price / 1_000_000;
            return acc;
          }, 0);

          // ✅ Calculate total unique owners
          const uniqueOwners = new Set(filteredNfts.map((nft) => nft.owner))
            .size;

          // Assign computed values
          collection.mainCollectionNFT.total_volume = totalVolumeCalculated;
          collection.mainCollectionNFT.owners_count = uniqueOwners;

          setMainCollectionNFT(collection.mainCollectionNFT);
          setNfts(filteredNfts);
          setCollection(res.data.collections[0]);
          setError(null);
        } else {
          setError("Failed to load collection data");
          setMainCollectionNFT(null);
          setNfts([]);
        }
      } catch (err) {
        setError(err.message);
        setMainCollectionNFT(null);
        setNfts([]);
      } finally {
        setLoading(false);
      }
    }

    if (collectionId) {
      fetchCollection();
    }
  }, [collectionId]);

  const generateAvatar = (walletAddress) =>
    `${generateAvatarurl}/${walletAddress}.png?size=64x64`;

  if (loading)
    return (
      <section className="jumbotron breadcumb no-bg">
        <div className="mainbreadcumb">
          <div className="container">
            <div>Loading collection...</div>
          </div>
        </div>
      </section>
    );
  if (error)
    return (
      <section className="jumbotron breadcumb no-bg">
        <div className="mainbreadcumb">
          <div className="container">
            <div>Error: {error}</div>{" "}
          </div>
        </div>
      </section>
    );

  const stats = {
    items: nfts.length,
    totalVolume: mainCollectionNFT?.total_volume || 0,
    volume24h: mainCollectionNFT?.volume_24h || 0,
    owners: mainCollectionNFT?.owners_count || 0,
    forSale: mainCollectionNFT?.open_to_sell === true ? 1 : 0,
    floorPrice: mainCollectionNFT?.price || 0,
  };

  return (
    <section className="jumbotron breadcumb no-bg">
      <div className="mainbreadcumb">
        <div className="container">
          <div>
            {/* Banner & Title */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 16,
                paddingBottom: 16,
                borderBottom: "1px solid #ccc",
                marginBottom: 24,
                padding: "10px",
                background: "#f6f3ee",
                borderRadius: 8,
                boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
              }}
            >
              <img
                src={
                  mainCollectionNFT?.ipfs_url?.replace(
                    "ipfs://",
                    "https://ipfs.io/ipfs/"
                  ) || "/img/collections/default.jpg"
                }
                alt={mainCollectionNFT?.name || "Collection Banner"}
                style={{
                  width: 140,
                  height: 140,
                  objectFit: "cover",
                  borderRadius: 8,
                }}
              />
              <div>
                <h1 style={{ margin: 0 }}>
                  {mainCollectionNFT?.name || "Untitled Collection"}
                </h1>
                <p style={{ margin: "4px 0", color: "#666", fontSize: 14 }}>
                  {mainCollectionNFT?.description || "Open editions"}
                </p>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginTop: 8,
                  }}
                >
                  <img
                    src={generateAvatar(mainCollectionNFT?.owner)}
                    alt="owner avatar"
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      border: "1px solid #ddd",
                    }}
                  />
                  <div style={{ fontSize: 14, color: "#333" }}>
                    Created by{" "}
                    <strong>
                      {mainCollectionNFT?.owner_username ||
                        mainCollectionNFT?.owner?.slice(0, 6) + "..."}
                    </strong>
                  </div>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
              {[
                { label: "Items", value: stats.items },
                { label: "Total Volume", value: `${stats.totalVolume} ꜩ` },
                { label: "24h Volume", value: `${stats.volume24h} ꜩ` },
                { label: "Owners", value: stats.owners },
                { label: "For Sale", value: stats.forSale },
                { label: "Floor Price", value: `${stats.floorPrice} ꜩ` },
              ].map(({ label, value }) => (
                <div
                  key={label}
                  style={{
                    flex: "1 1 0",
                    background: "#f6f3ee",
                    borderRadius: 6,
                    boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
                    padding: 12,
                    textAlign: "center",
                  }}
                >
                  <div style={{ fontWeight: "bold", fontSize: 20 }}>
                    {value}
                  </div>
                  <div style={{ fontSize: 12, color: "#666" }}>{label}</div>
                </div>
              ))}
            </div>

            {/* Tabs */}
            <div
              style={{
                borderBottom: "1px solid #ccc",
                marginBottom: 24,
                display: "flex",
                gap: 24,
                fontWeight: "bold",
                fontSize: 16,
                color: "#555",
              }}
            >
              <div
                style={{
                  borderBottom: "3px solid #000",
                  paddingBottom: 8,
                  cursor: "pointer",
                }}
              >
                Tokens
              </div>
            </div>

            {/* NFT Grid */}
            {nfts.length === 0 ? (
              <div>No bundled NFTs found in this collection.</div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))",
                  gap: 20,
                }}
              >
                {nfts.map((nft) => {
                  const imageUrl = nft.ipfs_url?.replace(
                    "ipfs://",
                    "https://ipfs.io/ipfs/"
                  );

                  const toTez = (mutez) => mutez / 1000000;
                  // Dummy time left & price, replace with your real data
                  const timeLeft = nft.timeLeft; // only show if exists
                  const price = toTez(nft.price || 0);

                  return (
                    <div
                      key={nft.base_id || nft.token_id || nft.id}
                      style={{
                        border: "1px solid #ddd",
                        borderRadius: 8,
                        overflow: "hidden",
                        boxShadow: "0 2px 6px rgb(0 0 0 / 0.1)",
                        cursor: "pointer",
                        backgroundColor: "white",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <img
                        src={imageUrl || "/img/collections/default.jpg"}
                        alt={nft.name || "NFT"}
                        style={{
                          width: "100%",
                          height: 140,
                          objectFit: "cover",
                        }}
                      />
                      {console.log(Collection)}
                      <div style={{ padding: 12, flexGrow: 1 }}>
                        <h4
                          style={{
                            margin: "0 0 6px 0",
                            fontSize: 16,
                            fontWeight: "600",
                            color: "#222",
                          }}
                          className="titlhov"
                          onClick={() => navigate(`/art/${nft.art_id}`)}
                        >
                          {nft.name || "Untitled NFT"}
                        </h4>
                        <div style={{ fontSize: 13, color: "#555" }}>
                          {mainCollectionNFT?.name || "In the open"}
                        </div>
                      </div>
                      <div
                        style={{
                          padding: "8px 12px",
                          borderTop: "1px solid #eee",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          fontSize: 13,
                          color: "#555",
                          backgroundColor: "#f8f8f8",
                          // boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <img
                            src={generateAvatar(nft.owner)}
                            alt="owner avatar"
                            style={{
                              width: 28,
                              height: 28,
                              borderRadius: "50%",
                            }}
                          />
                          <span>
                            {nft.owner?.slice(0, 6)}...{nft.owner?.slice(-4)}
                          </span>
                        </div>
                        <div>{price}ꜩ</div>
                      </div>
                      {/* <div
                        style={{
                          fontSize: 12,
                          padding: "6px 12px",
                          backgroundColor: "#0077ff",
                          color: "white",
                          fontWeight: "bold",
                          textAlign: "center",
                        }}
                      >
                        {timeLeft}
                      </div> */}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export default CollectionPage;
